import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { createPost, updatePost, getPost, deletePost, getCollections } from '../services/api';
import { ArrowLeft, Save, Trash2, Eye, Image as ImageIcon, Plus, X, FileText, Shield, Star, AlertCircle, Upload, Video, Play, Loader2, FolderOpen, Camera, Film, Gift, Lock, Clock, Calendar } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import ImageUpload from '../components/ImageUpload';
import VideoPlayer, { isValidVideoUrl, getVideoThumbnail, parseVideoUrl } from '../components/VideoPlayer';

const PostEditorPage = () => {
  const { isOwner } = useAuth();
  const navigate = useNavigate();
  const { postId } = useParams();
  const isEditing = !!postId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    images: [],
    vimeoEmbedUrls: [],
    externalLinks: [],
    minimumTierLevel: 0,
    status: 'draft',
    featured: false,
    archived: false,
    tags: [],
    isForSale: false,
    price: '',
    privateContent: '',
    postType: '',
    collectionId: ''
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newVimeoUrl, setNewVimeoUrl] = useState('');
  const [fetchingThumbnail, setFetchingThumbnail] = useState(false);
  const [errors, setErrors] = useState({});
  const [collections, setCollections] = useState([]);
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  // Rename field reference for clarity but keep backward compatible
  const videoUrls = formData.vimeoEmbedUrls || [];

  // Post type options
  const postTypes = [
    { value: 'none', label: 'None (No category)', icon: FileText, description: 'Post will not appear in collections' },
    { value: 'Photos', label: 'Photos', icon: Camera, description: 'Photo-based content' },
    { value: 'Videos', label: 'Videos', icon: Film, description: 'Video-based content' },
    { value: 'Bonus', label: 'Bonus', icon: Gift, description: 'Special bonus content' }
  ];

  const tierLevels = [
    { value: 0, label: 'Free', description: 'Available to all investigators' },
    { value: 1, label: 'Bronze', description: 'Bronze clearance and above' },
    { value: 2, label: 'Silver', description: 'Silver clearance and above' },
    { value: 3, label: 'Gold', description: 'Gold clearance only' }
  ];

  const loadCollections = useCallback(async () => {
    try {
      const data = await getCollections(false);
      setCollections(data);
    } catch (error) {
    }
  }, []);

  const loadPost = useCallback(async () => {
    try {
      setLoading(true);
      const post = await getPost(postId);
      setFormData({
        title: post.title || '',
        content: post.content || '',
        images: post.images || [],
        vimeoEmbedUrls: post.vimeoEmbedUrls || [],
        externalLinks: post.externalLinks || [],
        minimumTierLevel: post.minimumTierLevel || 0,
        status: post.status || 'draft',
        featured: post.featured || false,
        archived: post.archived || false,
        tags: post.tags || [],
        isForSale: post.isForSale || false,
        price: post.price ? post.price.toString() : '',
        privateContent: post.privateContent || '',
        postType: post.postType || '',
        collectionId: post.collectionId || ''
      });
      // Restore schedule state if post is scheduled
      if (post.status === 'scheduled' && post.scheduledPublishAt) {
        setShowScheduler(true);
        const dt = new Date(post.scheduledPublishAt.endsWith('Z') ? post.scheduledPublishAt : post.scheduledPublishAt + 'Z');
        setScheduleDate(dt.toISOString().split('T')[0]);
        setScheduleTime(dt.toTimeString().slice(0, 5));
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load case file',
        variant: 'destructive'
      });
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [postId, navigate]);

  useEffect(() => {
    if (isEditing) {
      loadPost();
    }
    loadCollections();
  }, [postId, isEditing, loadPost, loadCollections]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    if (formData.title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (publishStatus = null) => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before saving',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSaving(true);
      const dataToSave = {
        ...formData,
        status: publishStatus || formData.status,
        price: formData.isForSale && formData.price ? parseFloat(formData.price) : null,
        privateContent: formData.isForSale && formData.privateContent ? formData.privateContent : null,
        postType: formData.postType || null,
        collectionId: formData.collectionId || null,
        scheduledPublishAt: null
      };

      // Handle scheduled publishing
      if (publishStatus === 'scheduled' && scheduleDate && scheduleTime) {
        const scheduledUTC = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
        dataToSave.scheduledPublishAt = scheduledUTC;
      }

      if (isEditing) {
        await updatePost(postId, dataToSave);
        toast({
          title: 'Success',
          description: 'Case file updated successfully'
        });
      } else {
        await createPost(dataToSave);
        toast({
          title: 'Success',
          description: publishStatus === 'published' ? 'Case file published!' : publishStatus === 'scheduled' ? 'Case file scheduled!' : 'Case file saved as draft'
        });
      }
      navigate('/admin');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save case file',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this case file? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      await deletePost(postId);
      toast({
        title: 'Deleted',
        description: 'Case file has been permanently deleted'
      });
      navigate('/admin');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete case file',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const addImage = () => {
    if (newImageUrl.trim() && !formData.images.includes(newImageUrl.trim())) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImageUrl.trim()]
      }));
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    const tag = newTag.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const addVimeoUrl = async () => {
    const url = newVimeoUrl.trim();
    if (!url) return;
    
    if (!isValidVideoUrl(url)) {
      toast({
        title: 'Invalid URL',
        description: 'Please enter a valid Vimeo, YouTube, Bunny Stream, or Rumble URL',
        variant: 'destructive'
      });
      return;
    }
    
    if (formData.vimeoEmbedUrls.includes(url)) {
      toast({
        title: 'Duplicate',
        description: 'This video has already been added',
        variant: 'destructive'
      });
      return;
    }

    // Warn user if Rumble share URL was pasted — embed wont work
    const parsedPre = parseVideoUrl(url);
    if (parsedPre?.platform === 'rumble' && parsedPre.isShareUrl) {
      toast({
        title: 'Rumble share URL not embeddable',
        description: 'Open the video on Rumble, click the "Embed" button below the player, then paste the Embed URL or full iframe code here.',
        variant: 'destructive'
      });
      return;
    }
    
    setFetchingThumbnail(true);
    
    try {
      // Fetch thumbnail to use as post preview
      const thumbnail = await getVideoThumbnail(url);
      const parsed = parseVideoUrl(url);
      const platformName = parsed?.platform === 'youtube' ? 'YouTube' : parsed?.platform === 'bunny' ? 'Bunny Stream' : parsed?.platform === 'rumble' ? 'Rumble' : 'Vimeo';
      
      setFormData(prev => ({
        ...prev,
        vimeoEmbedUrls: [...prev.vimeoEmbedUrls, url],
        // Auto-add thumbnail as first image if no images exist
        images: thumbnail && prev.images.length === 0 
          ? [thumbnail, ...prev.images] 
          : prev.images
      }));
      
      setNewVimeoUrl('');
      
      toast({
        title: `${platformName} video added`,
        description: thumbnail 
          ? 'Video added and thumbnail set as preview image' 
          : 'Video added successfully'
      });
    } catch (error) {
      // Still add the video even if thumbnail fetch fails
      setFormData(prev => ({
        ...prev,
        vimeoEmbedUrls: [...prev.vimeoEmbedUrls, url]
      }));
      setNewVimeoUrl('');
      toast({
        title: 'Video added',
        description: 'Video added (thumbnail fetch failed)'
      });
    } finally {
      setFetchingThumbnail(false);
    }
  };

  const removeVimeoUrl = (index) => {
    setFormData(prev => ({
      ...prev,
      vimeoEmbedUrls: prev.vimeoEmbedUrls.filter((_, i) => i !== index)
    }));
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-red-600 mb-4" />
          <h1 className="text-2xl font-headline text-amber-500 mb-4 tracking-wider">ACCESS DENIED</h1>
          <p className="text-gray-400 font-typewriter mb-6">Only the platform owner can create case files.</p>
          <Button onClick={() => navigate('/feed')} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider">
            Back to Evidence Files
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black relative">
        <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
        <div className="container mx-auto px-4 py-16 text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-amber-500/70 font-typewriter">Loading case file...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative" data-testid="post-editor-page">
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
      
      <div className="container mx-auto px-4 py-8 max-w-4xl relative z-10">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-amber-500 hover:text-amber-400 hover:bg-amber-900/20"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Command Center
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {isEditing && (
              <Button 
                variant="outline" 
                onClick={handleDelete}
                disabled={saving}
                className="border-red-900 text-red-500 hover:bg-red-900/20 font-headline tracking-wider"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
            <Button 
              variant="outline"
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="border-amber-900 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>
            <div className="relative">
              <div className="flex">
                <Button 
                  onClick={() => handleSave('published')}
                  disabled={saving}
                  className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider rounded-r-none"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {saving ? 'Publishing...' : 'Publish Now'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowScheduler(!showScheduler)}
                  disabled={saving}
                  className={`rounded-l-none border-l-0 font-headline tracking-wider ${showScheduler ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600' : 'border-amber-600 text-amber-500 hover:bg-amber-900/20'}`}
                  title="Schedule for later"
                >
                  <Clock className="w-4 h-4" />
                </Button>
              </div>
              {showScheduler && (
                <div className="absolute right-0 top-full mt-2 z-50 bg-gray-900 border-2 border-blue-600/50 rounded-lg p-4 shadow-2xl w-72" data-testid="schedule-picker">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    <span className="font-headline text-blue-400 tracking-wider text-sm">SCHEDULE PUBLICATION</span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-gray-400 font-typewriter">Date</Label>
                      <Input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="bg-gray-800 border-blue-900/50 focus:border-blue-500 font-typewriter text-sm"
                        data-testid="schedule-date"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 font-typewriter">Time (your local time)</Label>
                      <Input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="bg-gray-800 border-blue-900/50 focus:border-blue-500 font-typewriter text-sm"
                        data-testid="schedule-time"
                      />
                    </div>
                    <Button
                      onClick={() => {
                        if (!scheduleDate || !scheduleTime) {
                          toast({ title: 'Missing Info', description: 'Please select both date and time', variant: 'destructive' });
                          return;
                        }
                        const scheduled = new Date(`${scheduleDate}T${scheduleTime}`);
                        if (scheduled <= new Date()) {
                          toast({ title: 'Invalid Time', description: 'Scheduled time must be in the future', variant: 'destructive' });
                          return;
                        }
                        handleSave('scheduled');
                      }}
                      disabled={saving || !scheduleDate || !scheduleTime}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-headline tracking-wider"
                      data-testid="schedule-confirm-btn"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      {saving ? 'Scheduling...' : 'Schedule Post'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Title Section */}
        <div className="mb-6">
          <div className="inline-block mb-4">
            <div className="bg-amber-900/20 border-2 border-amber-600 px-6 py-2 inline-flex items-center gap-3">
              <FileText className="w-6 h-6 text-amber-500" />
              <h1 className="text-3xl font-headline text-amber-500 tracking-wider">
                {isEditing ? 'EDIT CASE FILE' : 'NEW CASE FILE'}
              </h1>
            </div>
          </div>
          {formData.status === 'draft' && (
            <Badge className="ml-4 bg-gray-700 text-gray-300 font-headline tracking-wider">DRAFT</Badge>
          )}
          {formData.status === 'published' && (
            <Badge className="ml-4 bg-green-700 text-white font-headline tracking-wider">PUBLISHED</Badge>
          )}
          {formData.status === 'scheduled' && (
            <Badge className="ml-4 bg-blue-700 text-white font-headline tracking-wider">SCHEDULED</Badge>
          )}
        </div>

        {/* Main Form */}
        <div className="space-y-6">
          {/* Title Input */}
          <Card className="bg-gray-900/90 border-2 border-amber-900/30">
            <CardHeader>
              <CardTitle className="font-headline text-lg text-amber-500 tracking-wider">CASE TITLE</CardTitle>
              <CardDescription className="font-typewriter text-gray-400">
                Enter a compelling title for your investigation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="The Case of the Missing Evidence..."
                className={`bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter text-lg ${errors.title ? 'border-red-500' : ''}`}
                data-testid="post-title-input"
              />
              {errors.title && (
                <p className="mt-2 text-red-500 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.title}
                </p>
              )}
              <p className="mt-2 text-gray-500 text-sm font-typewriter">
                {formData.title.length}/200 characters
              </p>
            </CardContent>
          </Card>

          {/* Content Input */}
          <Card className="bg-gray-900/90 border-2 border-amber-900/30">
            <CardHeader>
              <CardTitle className="font-headline text-lg text-amber-500 tracking-wider">CASE DETAILS</CardTitle>
              <CardDescription className="font-typewriter text-gray-400">
                Write the full content of your case file
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Late last night, critical evidence emerged that could crack the case wide open..."
                rows={12}
                className={`bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter ${errors.content ? 'border-red-500' : ''}`}
                data-testid="post-content-input"
              />
              {errors.content && (
                <p className="mt-2 text-red-500 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.content}
                </p>
              )}
              <p className="mt-2 text-gray-500 text-sm font-typewriter">
                {formData.content.length} characters
              </p>
            </CardContent>
          </Card>

          {/* Images Section */}
          <Card className="bg-gray-900/90 border-2 border-amber-900/30">
            <CardHeader>
              <CardTitle className="font-headline text-lg text-amber-500 tracking-wider">EVIDENCE PHOTOS</CardTitle>
              <CardDescription className="font-typewriter text-gray-400">
                Upload images or add URLs for your case file
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Upload Image */}
              <div className="mb-6">
                <Label className="font-headline text-sm text-amber-600 tracking-wider mb-2 block">
                  <Upload className="w-4 h-4 inline mr-2" />
                  UPLOAD NEW EVIDENCE
                </Label>
                <ImageUpload
                  onUploadComplete={(url) => {
                    setFormData(prev => ({
                      ...prev,
                      images: [...prev.images, url]
                    }));
                  }}
                  label="Upload evidence photo"
                />
              </div>

              {/* Or add by URL */}
              <div className="mb-4">
                <Label className="font-headline text-sm text-gray-500 tracking-wider mb-2 block">
                  OR ADD BY URL
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="https://example.com/evidence-photo.jpg"
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addImage())}
                  />
                  <Button 
                    onClick={addImage}
                    className="bg-amber-600 hover:bg-amber-700 text-black"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {formData.images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {formData.images.map((url, index) => (
                    <div key={`img-${index}-${url.slice(-20)}`} className="relative group">
                      <img 
                        src={url} 
                        alt={`Evidence ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-amber-900/30 grayscale-[30%]"
                        style={{filter: 'sepia(0.2) contrast(1.05)'}}
                        onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error'}
                      />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <Badge className="absolute bottom-2 left-2 bg-black/70 font-typewriter text-xs">
                        #{index + 1}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              
              {formData.images.length === 0 && (
                <div className="border-2 border-dashed border-amber-900/30 rounded-lg p-8 text-center mt-4">
                  <ImageIcon className="w-12 h-12 mx-auto text-amber-900/50 mb-2" />
                  <p className="text-gray-500 font-typewriter">No evidence photos added yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Video Evidence Section */}
          <Card className="bg-gray-900/90 border-2 border-amber-900/30">
            <CardHeader>
              <CardTitle className="font-headline text-lg text-amber-500 tracking-wider">
                <div className="flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  VIDEO EVIDENCE
                </div>
              </CardTitle>
              <CardDescription className="font-typewriter text-gray-400">
                Embed Vimeo, YouTube, Bunny Stream, or Rumble videos to stream directly in your case file
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Label className="font-headline text-sm text-amber-600 tracking-wider mb-2 block">
                  ADD VIDEO (VIMEO, YOUTUBE, BUNNY STREAM, OR RUMBLE)
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={newVimeoUrl}
                    onChange={(e) => setNewVimeoUrl(e.target.value)}
                    placeholder="Paste Vimeo, YouTube, Bunny Stream, or Rumble URL..."
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVimeoUrl())}
                    data-testid="video-url-input"
                  />
                  <Button 
                    onClick={addVimeoUrl}
                    disabled={fetchingThumbnail}
                    className="bg-amber-600 hover:bg-amber-700 text-black"
                    data-testid="add-video-btn"
                  >
                    {fetchingThumbnail ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="mt-2 text-gray-500 text-xs font-typewriter">
                  Paste a Vimeo, YouTube, Bunny Stream, or Rumble URL. The video thumbnail will be used as the post preview image.
                </p>
                <p className="mt-1 text-amber-700/80 text-xs font-typewriter">
                  Rumble tip: use the <span className="text-amber-500">Embed</span> button under the video (not the share URL) — paste the Embed URL or full iframe code.
                </p>
              </div>
              
              {formData.vimeoEmbedUrls.length > 0 && (
                <div className="space-y-4 mt-4">
                  {formData.vimeoEmbedUrls.map((url, index) => (
                    <div key={`video-${index}-${url.slice(-20)}`} className="relative">
                      <VideoPlayer 
                        url={url} 
                        showThumbnail={true}
                        className="rounded-lg border-2 border-amber-900/30"
                      />
                      <button
                        onClick={() => removeVimeoUrl(index)}
                        className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors z-10"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <Badge className="absolute top-2 left-2 bg-black/70 font-typewriter text-xs z-10">
                        Video #{index + 1}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              
              {formData.vimeoEmbedUrls.length === 0 && (
                <div className="border-2 border-dashed border-amber-900/30 rounded-lg p-8 text-center mt-4">
                  <Video className="w-12 h-12 mx-auto text-amber-900/50 mb-2" />
                  <p className="text-gray-500 font-typewriter">No video evidence added yet</p>
                  <p className="text-gray-600 font-typewriter text-sm mt-1">
                    Supports Vimeo, YouTube, Bunny Stream &amp; Rumble
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Settings Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Access Level */}
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-lg text-amber-500 tracking-wider">CLEARANCE LEVEL</CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  Who can access this case file?
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select 
                  value={formData.minimumTierLevel.toString()}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, minimumTierLevel: parseInt(value) }))}
                >
                  <SelectTrigger className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-amber-900/50">
                    {tierLevels.map(tier => (
                      <SelectItem key={tier.value} value={tier.value.toString()} className="font-typewriter">
                        <div className="flex items-center gap-2">
                          <Badge className={`
                            ${tier.value === 0 ? 'bg-gray-600' : ''}
                            ${tier.value === 1 ? 'bg-amber-700' : ''}
                            ${tier.value === 2 ? 'bg-gray-400 text-black' : ''}
                            ${tier.value === 3 ? 'bg-yellow-500 text-black' : ''}
                          `}>
                            {tier.label}
                          </Badge>
                          <span className="text-gray-400 text-sm">{tier.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Featured & Archive Toggles */}
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-lg text-amber-500 tracking-wider">VISIBILITY</CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  Control how this case appears in the feeds
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Star className={`w-6 h-6 ${formData.featured ? 'text-yellow-500 fill-yellow-500' : 'text-gray-500'}`} />
                    <div>
                      <p className="font-headline text-gray-100 tracking-wide">Mark as Featured</p>
                      <p className="text-sm text-gray-500 font-typewriter">
                        Featured cases are pinned to the top of the main feed
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, featured: checked }))}
                    data-testid="featured-toggle"
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-800/50 border border-gray-700/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FolderOpen className={`w-6 h-6 ${formData.archived ? 'text-amber-500' : 'text-gray-500'}`} />
                    <div>
                      <p className="font-headline text-gray-100 tracking-wide">Archive</p>
                      <p className="text-sm text-gray-500 font-typewriter">
                        Archived cases only appear in their category tab, not the main feed
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.archived}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, archived: checked }))}
                    data-testid="archived-toggle"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Post Type & Collection Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Post Type */}
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-lg text-amber-500 tracking-wider">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-5 h-5" />
                    POST TYPE
                  </div>
                </CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  Categorize this post for collections (optional)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select 
                  value={formData.postType || 'none'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, postType: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter" data-testid="post-type-select">
                    <SelectValue placeholder="Select post type..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-amber-900/50">
                    {postTypes.map(type => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value} className="font-typewriter">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${type.value !== 'none' ? 'text-amber-500' : 'text-gray-500'}`} />
                            <span>{type.label}</span>
                            <span className="text-gray-500 text-xs ml-2">- {type.description}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {!formData.postType && (
                  <p className="mt-2 text-gray-500 text-xs font-typewriter">
                    Leave blank if you don't want this post in any collection category
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Collection Assignment */}
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-lg text-amber-500 tracking-wider">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-5 h-5" />
                    ASSIGN TO COLLECTION
                  </div>
                </CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  Add this post to a specific collection (optional)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select 
                  value={formData.collectionId || 'none'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, collectionId: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter" data-testid="collection-select">
                    <SelectValue placeholder="Select a collection..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-amber-900/50">
                    <SelectItem value="none" className="font-typewriter">
                      <div className="flex items-center gap-2">
                        <X className="w-4 h-4 text-gray-500" />
                        <span>No Collection</span>
                      </div>
                    </SelectItem>
                    {collections.map(collection => (
                      <SelectItem key={collection.id} value={collection.id} className="font-typewriter">
                        <div className="flex items-center gap-2">
                          <FolderOpen className="w-4 h-4 text-amber-500" />
                          <span>{collection.title}</span>
                          {!collection.isPublished && (
                            <Badge variant="outline" className="text-xs ml-2">Draft</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {collections.length === 0 && (
                  <p className="mt-2 text-gray-500 text-xs font-typewriter">
                    No collections created yet. Create one in Admin &gt; Collections
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sell Post Section */}
          <Card className="bg-gray-900/90 border-2 border-amber-900/30">
            <CardHeader>
              <CardTitle className="font-headline text-lg text-amber-500 tracking-wider">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                  SELL THIS CASE FILE
                </div>
              </CardTitle>
              <CardDescription className="font-typewriter text-gray-400">
                Enable one-time purchase for this content
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${formData.isForSale ? 'bg-green-900/50 text-green-500' : 'bg-gray-800 text-gray-500'}`}>
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-headline text-gray-100 tracking-wide">Enable Individual Purchase</p>
                    <p className="text-sm text-gray-500 font-typewriter">
                      Allow users to buy this case file separately
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.isForSale}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    isForSale: checked,
                    price: checked ? prev.price || '4.99' : ''
                  }))}
                />
              </div>

              {formData.isForSale && (
                <div className="space-y-3 p-4 bg-green-900/10 border border-green-900/30 rounded-lg">
                  <Label className="font-headline text-sm text-green-500 tracking-wider">
                    PRICE (USD)
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500 font-headline text-lg">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.50"
                      max="999.99"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="4.99"
                      className="bg-gray-800 border-green-900/50 focus:border-green-500 font-typewriter pl-8 text-lg"
                    />
                  </div>
                  <p className="text-xs text-gray-500 font-typewriter">
                    Set a price between $0.50 and $999.99. Users can purchase this case file without a subscription.
                  </p>
                </div>
              )}

              {formData.isForSale && (
                <div className="space-y-3 p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
                  <Label className="font-headline text-sm text-amber-500 tracking-wider">
                    <Lock className="w-4 h-4 inline mr-2" />
                    PRIVATE CONTENT (REVEALED AFTER PURCHASE)
                  </Label>
                  <Textarea
                    value={formData.privateContent}
                    onChange={(e) => setFormData(prev => ({ ...prev, privateContent: e.target.value }))}
                    placeholder="Add exclusive content here (e.g., download links, bonus material, secret codes)...&#10;&#10;This content will ONLY be visible to users who have purchased this case file."
                    rows={6}
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    data-testid="private-content-input"
                  />
                  <p className="text-xs text-gray-500 font-typewriter">
                    This content is hidden until the user completes their purchase. Use it for download links, bonus material, or any exclusive content.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tags Section */}
          <Card className="bg-gray-900/90 border-2 border-amber-900/30">
            <CardHeader>
              <CardTitle className="font-headline text-lg text-amber-500 tracking-wider">CASE TAGS</CardTitle>
              <CardDescription className="font-typewriter text-gray-400">
                Add tags to help categorize your case file
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag (e.g., murder, cold-case, breaking)"
                  className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button 
                  onClick={addTag}
                  className="bg-amber-600 hover:bg-amber-700 text-black"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <Badge 
                      key={tag} 
                      className="bg-amber-900/30 text-amber-500 border border-amber-700 font-typewriter flex items-center gap-1 pr-1"
                    >
                      #{tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-1 hover:text-red-500 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PostEditorPage;
